/**
 * Configuración para el menú de grupos y subgrupos
 */

/**
 * Configuración general del menú
 */
export const MENU_CONFIG = {

  // Configuración de navegación
  navigation: {
    showBreadcrumb: true,
    enableDeepLinking: false,
    rememberExpansionState: true,

    // Configuración de desplazamiento al seleccionar grupo/subgrupo
    // Cuando true, la vista se desplazará hacia arriba al cambiar el grupo seleccionado
    scrollToTopOnGroupSelect: true,
    // Comportamiento del desplazamiento: 'smooth' o 'instant'
    scrollBehavior: 'smooth',

    // Nuevo: comportamiento de clic unificado
    clickBehavior: {
      // Cuando true, el clic principal (fila completa) y la flecha hacen lo mismo
      unifyClickArea: false,
      // Destino principal del clic: 'row' o 'arrow' (solo informativo)
      primaryClickTarget: 'arrow',
      // Si el elemento tiene subgrupos/grupos hijos, el clic expande/colapsa y NO selecciona
      expandOnClickWhenHasChildren: true,
      // Si el elemento es hoja (sin hijos), selecciona para mostrar preguntas
      selectLeafOnClick: true
    }
  },


  // Configuración de filtros y visualización de evaluación de relevant
  relevantFiltering: {
    // 1) aplicar filtros relevant: cuando true, oculta/muestra según evaluación
    applyRelevantFilters: true,
    // 2) mostrar resultado de evaluación con colores: rojo, verde, blanco
    showEvaluationColors: true,
    // 3) mostrar un badge de depuración con el estado de la condición evaluada
    showDebugConditionBadge: false,
    // 4) mostrar la expresión raw de relevant en el tooltip del badge
    showRawRelevantExpression: false,
    // 5) mostrar valores actuales vs esperados en el tooltip (si no se muestra el raw)
    showDebugValues: true,
    // 6) longitud máxima de la expresión a mostrar en tooltip
    maxExpressionLength: 140
  },

  // Configuración de filtrado por nombre de grupo
  groupNameFiltering: {
    // Activar/desactivar el filtrado por nombre de grupo
    enabled: true,
    
    // Usar como whitelist (true) o blacklist (false)
    // false = ocultar grupos en la lista
    // true = mostrar solo grupos en la lista
    useAsWhitelist: false,
    
    // Lista de nombres de grupos a ocultar (cuando useAsWhitelist = false)
    // o lista de nombres de grupos a mostrar (cuando useAsWhitelist = true)
    list: [
      'info_parcela',
      'inicio_manual_grupo',
      'privacidad_grupo',
      'encuesta_admin',
      'sa_acta_acuerdo_cert_grupo',
      'productor_datos_nuevos_grupo'
    ],
    
    // Usar coincidencia exacta o parcial
    exactMatch: true,
    
    // Usar case sensitive o insensitive
    caseSensitive: false,
    
    // También filtrar por path del grupo (para grupos anidados)
    includeGroupPath: true,
    
    // Configuración de debug (apagado por defecto: esto se evalúa por cada grupo en
    // cada render del menú, así que dejarlo en true en producción genera ruido constante
    // en consola). Actívalo localmente si necesitas depurar el filtrado por nombre de grupo.
    debug: {
      enabled: false,
      showInConsole: true
    }
  }
};

/**
 * Evalúa si un grupo debe ser filtrado basándose en su nombre
 * @param {Object} grupo - Objeto del grupo con propiedades como name
 * @param {Object} filterConfig - Configuración de filtrado (opcional, usa MENU_CONFIG por defecto)
 * @returns {boolean} - true si el grupo debe ser ocultado, false si debe mostrarse
 */
export const shouldFilterGroupByName = (grupo, filterConfig = null) => {
  const config = filterConfig || MENU_CONFIG.groupNameFiltering;
  
  // Si el filtrado está deshabilitado, no filtrar
  if (!config.enabled) {
    return false;
  }
  
  // Si no hay lista de filtros, no filtrar
  if (!config.list || config.list.length === 0) {
    return false;
  }
  
  // Si no hay grupo o nombre, no filtrar
  if (!grupo || !grupo.name) {
    return false;
  }
  
  const groupName = config.caseSensitive ? grupo.name : grupo.name.toLowerCase();
  
  // Verificar si el nombre del grupo está en la lista
  const isInList = config.list.some(filterName => {
    const compareWith = config.caseSensitive ? filterName : filterName.toLowerCase();
    
    if (config.exactMatch) {
      return groupName === compareWith;
    } else {
      return groupName.includes(compareWith);
    }
  });
  
  // También verificar por path si está habilitado
  let isInPathList = false;
  if (config.includeGroupPath && grupo.path) {
    const groupPath = config.caseSensitive ? grupo.path : grupo.path.toLowerCase();
    isInPathList = config.list.some(filterName => {
      const compareWith = config.caseSensitive ? filterName : filterName.toLowerCase();
      
      if (config.exactMatch) {
        return groupPath.includes(`/${compareWith}/`) || groupPath.endsWith(`/${compareWith}`);
      } else {
        return groupPath.includes(compareWith);
      }
    });
  }
  
  const matchFound = isInList || isInPathList;
  
  // Debug logging
  if (config.debug && config.debug.enabled && config.debug.showInConsole) {
    console.log(`🔍 [GROUP FILTER] Grupo: "${grupo.name}", En lista: ${matchFound}, Acción: ${config.useAsWhitelist ? (matchFound ? 'MOSTRAR' : 'OCULTAR') : (matchFound ? 'OCULTAR' : 'MOSTRAR')}`);
  }
  
  // Si useAsWhitelist es true: ocultar si NO está en la lista
  // Si useAsWhitelist es false: ocultar si SÍ está en la lista
  return config.useAsWhitelist ? !matchFound : matchFound;
};

export default MENU_CONFIG;